import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, TrendingUp, AlertTriangle, CheckCircle, Play, Database } from 'lucide-react';
import { fetchConversionRunSummary } from '../data/adapters';
import { ConversionRunSummary } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const ConversionOpsTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [data, setData] = useState<ConversionRunSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const conversionData = await fetchConversionRunSummary(isDemo).catch(() => null);
                setData(conversionData);
                if (!conversionData) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
                {/* Conversion Metrics Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData || !data) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Runs Today</h3>
                            <Play size={20} className="text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Success Rate</h3>
                            <CheckCircle size={20} className="text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">P95 Duration</h3>
                            <Clock size={20} className="text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--m</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Rerun Rate</h3>
                            <TrendingUp size={20} className="text-orange-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">
                        Stage Duration Analysis
                    </h3>
                    {renderNoDataPlaceholder('Stage duration data')}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">Backlog Aging</h3>
                        {renderNoDataPlaceholder('Backlog data')}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            Performance Insights
                        </h3>
                        {renderNoDataPlaceholder('Performance data')}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                    {renderNoDataPlaceholder('Recommendations')}
                </motion.div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Runs Today */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Runs Today</h3>
                        <Play size={20} className="text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.runsToday}</div>
                    <p className="text-sm text-slate-400">Conversion jobs executed</p>
                </motion.div>

                {/* Success Rate */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Success Rate</h3>
                        <CheckCircle size={20} className="text-green-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.successRatePct}%</div>
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                        <div
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${data.successRatePct}%` }}
                        />
                    </div>
                    <p className="text-sm text-slate-400">Jobs completed successfully</p>
                </motion.div>

                {/* P95 Duration */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">P95 Duration</h3>
                        <Clock size={20} className="text-purple-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.p95DurationMin}m</div>
                    <p className="text-sm text-slate-400">95th percentile runtime</p>
                </motion.div>

                {/* Rerun Rate */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Rerun Rate</h3>
                        <TrendingUp size={20} className="text-orange-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.rerunRatePct}%</div>
                    <p className="text-sm text-slate-400">Jobs requiring reruns</p>
                </motion.div>
            </div>

            {/* Stage Duration Analysis */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Stage Duration Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data.avgStageDurations.map((stage, index) => (
                        <motion.div
                            key={stage.stage}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center"
                        >
                            <div className="text-sm text-slate-400 mb-2 capitalize">
                                {stage.stage}
                            </div>
                            <div className="text-2xl font-bold text-white mb-2">
                                {stage.avgMin.toFixed(1)}m
                            </div>
                            <div className="text-xs text-slate-400">average duration</div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Backlog Aging */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Backlog Distribution */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Backlog Aging</h3>
                    <div className="space-y-4">
                        {data.backlogAging.map(bucket => (
                            <div
                                key={bucket.bucket}
                                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                                <div className="flex items-center space-x-3">
                                    <div
                                        className={`w-3 h-3 rounded-full ${
                                            bucket.bucket === '0-1d'
                                                ? 'bg-green-400'
                                                : bucket.bucket === '1-3d'
                                                  ? 'bg-yellow-400'
                                                  : bucket.bucket === '3-7d'
                                                    ? 'bg-orange-400'
                                                    : 'bg-red-400'
                                        }`}
                                    />
                                    <span className="text-slate-300">{bucket.bucket}</span>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-white">
                                        {bucket.count}
                                    </div>
                                    <div className="text-sm text-slate-400">items</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Performance Insights */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Performance Insights</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                            <span className="text-slate-300">Total Backlog</span>
                            <div className="text-right">
                                <div className="text-lg font-semibold text-white">
                                    {data.backlogAging.reduce((sum, b) => sum + b.count, 0)}
                                </div>
                                <div className="text-sm text-slate-400">items</div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                            <span className="text-slate-300">Efficiency Score</span>
                            <div className="text-right">
                                <div className="text-lg font-semibold text-white">
                                    {Math.round(
                                        (100 - data.rerunRatePct) * (data.successRatePct / 100)
                                    )}
                                    %
                                </div>
                                <div className="text-sm text-slate-400">overall</div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                            <span className="text-slate-300">Throughput</span>
                            <div className="text-right">
                                <div className="text-lg font-semibold text-white">
                                    {Math.round(data.runsToday * (data.successRatePct / 100))}
                                </div>
                                <div className="text-sm text-slate-400">successful/day</div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Recommendations */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.successRatePct < 90 && (
                        <div className="flex items-start space-x-3 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                            <CheckCircle size={16} className="text-yellow-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-yellow-300">
                                    Improve Success Rate
                                </h4>
                                <p className="text-sm text-yellow-200">
                                    Current success rate is below 90%. Investigate common failure
                                    patterns.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.rerunRatePct > 10 && (
                        <div className="flex items-start space-x-3 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                            <TrendingUp size={16} className="text-orange-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-orange-300">Reduce Rerun Rate</h4>
                                <p className="text-sm text-orange-200">
                                    {data.rerunRatePct}% rerun rate indicates data quality or
                                    process issues.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.backlogAging.some(b => b.bucket === '>7d' && b.count > 0) && (
                        <div className="flex items-start space-x-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                            <AlertTriangle size={16} className="text-red-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-red-300">Address Aging Backlog</h4>
                                <p className="text-sm text-red-200">
                                    Items aging beyond 7 days require immediate attention.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.p95DurationMin > 30 && (
                        <div className="flex items-start space-x-3 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                            <Clock size={16} className="text-blue-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-blue-300">Optimize Performance</h4>
                                <p className="text-sm text-blue-200">
                                    P95 duration of {data.p95DurationMin}m suggests performance
                                    optimization needed.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.successRatePct >= 90 &&
                        data.rerunRatePct <= 10 &&
                        data.backlogAging.every(b => b.bucket !== '>7d' || b.count === 0) && (
                            <div className="flex items-start space-x-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg md:col-span-2">
                                <CheckCircle size={16} className="text-green-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-green-300">
                                        Excellent Conversion Performance
                                    </h4>
                                    <p className="text-sm text-green-200">
                                        All conversion metrics are within acceptable ranges.
                                        Continue monitoring for any degradation.
                                    </p>
                                </div>
                            </div>
                        )}
                </div>
            </motion.div>
        </div>
    );
};
